import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Search } from 'lucide-react';
import { GuestBottomNav } from '../components/GuestBottomNav';
import { MessengerListShell } from '../components/messaging/MessengerListShell';
import { MessengerListRow } from '../components/messaging/MessengerListRow';
import NotificationBellButton from '../components/NotificationBellButton';
import { formatChatDisplayName } from '../utils/nameFormatters';
import { buildMessageHeaders } from '../utils/messageHeaders';
import { primeCallPermissions } from '../utils/callPermissions';
import { SupportRowBadge } from '../components/messaging/SupportSystemCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getGuestConversationAvatar = (conversation = {}) => {
  if (conversation.other_user?.image_url) return conversation.other_user.image_url;
  if (conversation.other_user?.photo_url) return conversation.other_user.photo_url;
  const name = conversation.other_user?.name || '';
  if (name.includes('Taverna') || name.includes('Opa')) return 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=100&h=100&fit=crop';
  if (name.includes('Blue') || name.includes('Bar')) return 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=100&h=100&fit=crop';
  if (name.includes('Caf')) return 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=100&h=100&fit=crop';
  return null;
};

export default function ChatPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    primeCallPermissions('guest');
    const fetchConversations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/messages/conversations`, {
          headers: buildMessageHeaders(token, null, 'guest'),
        });
        setConversations(response.data || []);
      } catch (error) {
        console.error('Failed to load guest conversations', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, []);

  const filteredConversations = useMemo(() => conversations.filter((conversation) => {
    const name = formatChatDisplayName(conversation.other_user) || '';
    const type = conversation.type || conversation.other_user?.role || 'all';
    const matchesSearch = !search || name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' || type === filter || (filter === 'support' && conversation.type === 'support');
    return matchesSearch && matchesFilter;
  }), [conversations, filter, search]);

  const filterLabel = filter === 'all' ? 'All messages' : filter === 'venue' ? 'Venues' : filter === 'support' ? 'Support' : 'Hosts';

  const filterNode = (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
      <button
        onClick={() => setFilterOpen((prev) => !prev)}
        data-testid="guest-messages-filter-button"
        className="premium-card-compact premium-card-nested"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', cursor: 'pointer' }}
      >
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text)' }}>{filterLabel}</span>
        <ChevronDown size={16} color="#000" style={{ transform: filterOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }} />
      </button>
      {filterOpen ? (
        <div className="premium-card-compact premium-card-nested" style={{ position: 'absolute', top: '100%', right: 0, zIndex: 20, minWidth: 150, marginTop: 6, overflow: 'hidden' }}>
          {[
            ['all', 'All messages'],
            ['venue', 'Venues'],
            ['host', 'Hosts'],
            ['support', 'Support'],
          ].map(([value, label]) => (
            <button
              key={value}
              data-testid={`guest-messages-filter-${value}`}
              onClick={() => { setFilter(value); setFilterOpen(false); }}
              style={{ width: '100%', textAlign: 'left', padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: "'Inria Serif', serif", fontSize: 15, color: filter === value ? 'var(--accent-purple)' : '#666' }}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <MessengerListShell
      pageTestId="guest-messages-page"
      bellNode={<NotificationBellButton buttonTestId="guest-messages-bell-button" popupTestId="guest-messages-notification-popup" />}
      bottomNav={<GuestBottomNav />}
      title="Messages"
      searchValue={search}
      onSearchChange={(event) => setSearch(event.target.value)}
      searchPlaceholder="Venue or hosts’ name"
      searchTestId="guest-messages-search-input"
      searchIcon={<Search size={18} color="#8A8797" />}
      cardHeader={filterNode}
    >
      {loading ? <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-2)', textAlign: 'center', paddingTop: 20 }}>Loading conversations...</p> : null}
      {!loading && filteredConversations.length === 0 ? <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-2)', textAlign: 'center', paddingTop: 20 }}>No messages yet</p> : null}

      {filteredConversations.map((conversation) => (
        <MessengerListRow
          key={conversation.id}
          name={conversation.type === 'support' ? 'Allure' : (formatChatDisplayName(conversation.other_user) || 'Conversation')}
          preview={conversation.last_message || 'Open conversation'}
          imageUrl={conversation.type === 'support' ? '/notification-icon-192.png' : getGuestConversationAvatar(conversation)}
          unreadCount={conversation.unread_count || 0}
          timestamp={conversation.last_message_at}
          previewTone={conversation.last_message_kind}
          rowBadge={conversation.type === 'support' ? (
            <SupportRowBadge
              status={conversation.support_status}
              dataTestId={`guest-message-conversation-${conversation.id}-support-badge`}
            />
          ) : null}
          dataTestId={`guest-message-conversation-${conversation.id}`}
          onClick={() => navigate(`/guest/chat/${conversation.id}`)}
        />
      ))}
    </MessengerListShell>
  );
}